import React, { createContext, useContext, useEffect } from 'react'
import useReducerWithCallback from './useReducerWithCallback'
import crypto from 'crypto'

import { PAGE_URL, SCHEDULING_PAY_STATUS } from 'src/utils'
import MODAL from '../utils/modals'

import {
  pay,
  changePaymentMethod
} from '../services/schedulingService'

const CREDIT_CARD_PUBLIC_KEY = process.env.CREDIT_CARD_PUBLIC_KEY

export const PaymentContext = createContext()

const INITIAL_PAYMENT_STATE = {
  schedulingList: [],
  address: undefined,
  selectedCard: undefined,
  modal: undefined
}

const reducer = (state, action) => {
  switch (action.type) {
    case actionTypes.UPDATE_STATE:
      return { ...state, ...action.payload }
    case actionTypes.RESET_STATE:
      return INITIAL_PAYMENT_STATE
    default:
      return state
  }
}

export const actionTypes = {
  UPDATE_STATE: 'UPDATE_STATE',
  RESET_STATE: 'RESET_STATE'
}

const saveStateToLocalStorage = (state) => {
  try {
    const serializedState = JSON.stringify(state)
    window.localStorage.setItem('paymentState', serializedState) // eslint-disable-line
  } catch (err) {
    console.error('Erro ao salvar no localStorage:', err)
  }
}

const loadStateFromLocalStorage = () => {
  try {
    const serializedState = window.localStorage.getItem('paymentState') // eslint-disable-line
    return typeof serializedState !== 'undefined' ? JSON.parse(serializedState) : undefined
  } catch {
    return undefined
  }
}

export function PaymentContextProvider ({ children }) {
  const initialState = loadStateFromLocalStorage() || INITIAL_PAYMENT_STATE
  const [state, dispatch] = useReducerWithCallback(reducer, initialState)

  useEffect(() => {
    saveStateToLocalStorage(state)
  }, [state])

  const confirmAddress = (address) => {
    dispatch({
      type: actionTypes.UPDATE_STATE,
      payload: {
        address
      }
    })
  }

  const updateSchedulings = (schedulingList) => {
    dispatch({
      type: actionTypes.UPDATE_STATE,
      payload: {
        schedulingList
      }
    })
  }

  const storeCreditCard = (props) => {
    const { values, newCard, router } = props
    let encryptCard
    let payload

    if (newCard) {
      encryptCard = encryptCreditCard(values)
      payload = {
        encryptCard,
        brand: values.brand,
        schedulings: values.schedulings,
        lastDigits: values.cardNumber.substring(
          values.cardNumber.length - 4,
          values.cardNumber.length
        ),
        storeCard: values.storeCard
      }
    } else {
      payload = {
        cardHash: values.hash,
        brand: values.brand,
        lastDigits: values.lastDigits,
        schedulings: values.schedulings
      }
    }

    dispatch({
      type: actionTypes.UPDATE_STATE,
      payload: {
        card: {
          ...payload
        }
      }
    })

    router.push(PAGE_URL.CHECKOUT_PAYMENT_REVIEW)
  }

  const encryptCreditCard = (card) => {
    const { schedulings, ...cardData } = card
    const buffer = Buffer.from(JSON.stringify(cardData), 'utf8')
    const creditCardPublicKey = Buffer.from(CREDIT_CARD_PUBLIC_KEY, 'base64').toString('utf-8')
    const encrypted = crypto.publicEncrypt(creditCardPublicKey.replace(/\\n/g, '\n'), buffer)
    return encrypted.toString('base64')
  }

  // fecha modal de erro
  const handleModalClose = () => {
    dispatch({
      type: actionTypes.UPDATE_STATE,
      payload: {
        modal: undefined
      }
    })
  }

  // retorna modal de erro
  const showSchedulingFailureModal = ({ id, multi, router }) => {
    dispatch({
      type: actionTypes.UPDATE_STATE,
      payload: {
        modal: MODAL.PAYMENT_PROBLEM({
          onCancel: () => {
            handleSetLocalPayment({ id, multi, router })
            handleModalClose()
          },
          onConfirm: () => {
            handleReviewInformation({ router })
            handleModalClose()
          },
          onClose: () => {
            handleModalClose()
          }
        })
      }
    })
  }

  const handleReviewInformation = ({ router }) => {
    router.push(PAGE_URL.CHECKOUT_CARD)
  }

  const handleSetLocalPayment = async ({ id, multi, router }) => {
    try {
      await changePaymentMethod(id)
      handlePushToResume({ id, multi, router })
    } catch (error) {
      console.log(error)
    }
  }

  const showSucessModal = ({ id, multi, router }) => {
    dispatch({
      type: actionTypes.UPDATE_STATE,
      payload: {
        modal: MODAL.PAYMENT_SUCCESS({
          onConfirm: () => {
            handlePushToResume({ id, multi, router })
            handleModalClose()
          }
        })
      }
    })
  }

  const showPaymentProcessingModal = ({ router }) => {
    dispatch({
      type: actionTypes.UPDATE_STATE,
      payload: {
        modal: MODAL.PAYMENT_PROCESSING({
          onConfirm: () => {
            handlePushToSchedulings({ router })
            handleModalClose()
          }
        })
      }
    })
  }

  const handlePushToResume = ({ id, multi, router }) => {
    router.push(`${PAGE_URL.SCHEDULING_SUMMARY}/${id}${multi}`)
  }

  const handlePushToSchedulings = ({ router }) => {
    router.push(`${PAGE_URL.SCHEDULING_DIARY}`)
  }

  const handleConfirmPay = async ({ state, router }) => {
    const { address, card, schedulingList } = state
    const sessionId = window.Konduto?.getVisitorID()
    const multi = schedulingList.length > 1 ? '?multi=true' : ''
    const userBirthDate = window.localStorage.getItem('userBirthDate')

    const formatedAddress = {
      street: address.street,
      number: address.number,
      zipCode: address.postalCode,
      district: address.neighborhood,
      city: address.city,
      state: address.state,
      country: address.country,
      type: address.addressType
    }

    const creditCard = card.cardHash ? { cardHash: card.cardHash } : { encryptedCard: card.encryptCard }

    const payload = {
      userBirthDate,
      address: formatedAddress,
      creditCard,
      storeCard: card?.storeCard,
      schedulings: card.schedulings,
      sessionId
    }

    try {
      if (userBirthDate && formatedAddress && creditCard && card.schedulings.length) {
        const paymentResponse = await pay(payload)

        if (paymentResponse.success) {
          if (paymentResponse.data[0].payments.online.paymentStatus === SCHEDULING_PAY_STATUS.PAYMENT_PRE_AUTHORIZED) {
            showSucessModal({ id: schedulingList[0].id, multi, router })
          } else if (paymentResponse.data[0].payments.online.paymentStatus === SCHEDULING_PAY_STATUS.PAYMENT_PENDENT) {
            showPaymentProcessingModal({ router })
          } else if (paymentResponse.data[0].payments.online.paymentStatus === SCHEDULING_PAY_STATUS.PAYMENT_NOT_AUTHORIZED) {
            showSchedulingFailureModal({ router })
          }
        } else {
          console.error('Erro no pagamento', paymentResponse.error)
          showSchedulingFailureModal({ id: schedulingList[0].id, multi, router })
        }
      } else {
        console.log('Informações insuficientes para realizar o pagamento')
        showSchedulingFailureModal({ id: schedulingList[0].id, multi, router })
      }
    } catch (error) {
      console.error('Erro ao tentar realizar o pagamento', error)
      showSchedulingFailureModal()
    }
  }

  const functions = {
    confirmAddress,
    updateSchedulings,
    storeCreditCard,
    handleConfirmPay
  }

  return (
    <PaymentContext.Provider value={{ functions, state }}>
      {children}
    </PaymentContext.Provider>
  )
}

// Hook para acessar o contexto
export function usePaymentContext () {
  const context = useContext(PaymentContext)
  if (!context) {
    throw new Error('usePaymentContext deve ser usado dentro de um Provider!')
  }

  const { functions, state } = context
  return { functions, state }
}
